import axiosInstance from '@/app/api/axiosInstance'
// Import frontend types for function signatures, but backend types define the payload structure
import { TaxRate, TaxRateCreate, TaxRateUpdate } from '@/app/types/taxRate'
import { TaxRateComponentLink } from '@/app/types/taxComponent'
import { PaginatedResponse, ListQueryParams } from '@/app/types/api'
import { createOrganizationFilter } from '@/app/utils/filterUtils'
import axios from 'axios'

// Helper to prepare payload, ensuring expected fields and string numbers
const preparePayload = (data: TaxRateCreate | TaxRateUpdate): Record<string, unknown> => {
  // Create a fresh object - do NOT spread the original data to avoid unexpected issues
  const payload: Record<string, unknown> = {
    name: data.name,
    code: data.code,
    is_active: data.is_active
  };
  
  // Format rate as string for backend Decimal conversion
  if (data.rate !== undefined) {
    payload.rate = String(data.rate);
  }
  
  // Handle components - ensure rates are formatted as strings
  if (data.components) {
    payload.components = data.components.map(comp => ({
      tax_component_id: comp.tax_component_id,
      rate: String(comp.rate || 0)
    }));
  }
  
  return payload;
};

// Helper function to parse numeric fields from string responses
const parseTaxRateResponse = (rate: TaxRate): TaxRate => {
  const parsedRate = { ...rate }
  
  // Parse rates as numbers
  parsedRate.rate = parseFloat(String(parsedRate.rate || 0));
  
  if (parsedRate.components) {
    parsedRate.components = parsedRate.components.map(compDetail => ({
      ...compDetail,
      rate: parseFloat(String(compDetail.rate || 0))
    }));
  }
  
  return parsedRate;
};

export const listTaxRates = async (
  params: ListQueryParams,
  organizationId: number
): Promise<PaginatedResponse<TaxRate>> => {
  try {
    const requestParams = {
      ...params,
      ...(params.filters && typeof params.filters === 'string'
        ? { filters: params.filters }
        : organizationId
          ? { filters: createOrganizationFilter(organizationId) }
          : {}),
      include_global: true
    }
    const response = await axiosInstance.get<PaginatedResponse<TaxRate>>('/tax-rates/', {
      params: requestParams
    })
    // Parse response items
    response.data.items = response.data.items.map(parseTaxRateResponse)
    return response.data
  } catch (error) {
    console.error('Failed to list tax rates:', error)
    throw error
  }
};

export const getTaxRate = async (taxRateId: number): Promise<TaxRate> => {
  try {
    const response = await axiosInstance.get<TaxRate>(`/tax-rates/${taxRateId}`)
    return parseTaxRateResponse(response.data)
  } catch (error) {
    console.error(`Failed to get tax rate with ID ${taxRateId}:`, error)
    throw error
  }
};

// Accepts data matching frontend TaxRateCreate structure (which might include fields not sent)
export const createTaxRate = async (
  data: TaxRateCreate,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  organizationId: number // Kept for backward compatibility with existing code
): Promise<TaxRate> => {
  try {
    // Log the original data for debugging
    console.log('Original create data:', JSON.stringify(data, null, 2));
    
    // Prepare payload with proper string formatting for decimal values
    const payload = preparePayload(data);
    console.log('Sending payload to createTaxRate:', JSON.stringify(payload, null, 2));
    
    const response = await axiosInstance.post<TaxRate>('/tax-rates/', payload);
    return parseTaxRateResponse(response.data);
  } catch (error) {
    console.error('Failed to create tax rate:', error);
    if (axios.isAxiosError(error) && error.response) {
      console.error('Error status:', error.response.status);
      console.error('Error response data:', JSON.stringify(error.response.data, null, 2));
      if (error.response.data?.detail) {
        console.error('Backend validation error:', JSON.stringify(error.response.data.detail, null, 2));
      }
    }
    throw error;
  }
};

// Accepts data matching frontend TaxRateUpdate structure
export const updateTaxRate = async (taxRateId: number, data: TaxRateUpdate): Promise<TaxRate> => {
  try {
    // Log the original data for debugging
    console.log('Original update data:', JSON.stringify(data, null, 2));
    
    // Prepare payload with proper string formatting for decimal values
    const payload = preparePayload(data);
    console.log(`Sending payload to updateTaxRate (ID: ${taxRateId}):`, JSON.stringify(payload, null, 2));
    
    const response = await axiosInstance.patch<TaxRate>(`/tax-rates/${taxRateId}`, payload);
    return parseTaxRateResponse(response.data);
  } catch (error) {
    console.error(`Failed to update tax rate with ID ${taxRateId}:`, error);
    if (axios.isAxiosError(error) && error.response) {
      console.error('Error status:', error.response.status);
      console.error('Error response data:', JSON.stringify(error.response.data, null, 2));
      if (error.response.data?.detail) {
        console.error('Backend validation error:', JSON.stringify(error.response.data.detail, null, 2));
      }
    }
    throw error;
  }
};

export const deleteTaxRate = async (taxRateId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/tax-rates/${taxRateId}`)
  } catch (error) {
    console.error(`Failed to delete tax rate with ID ${taxRateId}:`, error)
    throw error
  }
};
